<template>
  <div class="action-menu flex">
    <slot name="trigger" :open="open" :disabled="disable">
      <q-btn
        :icon="undefined"
        :color="buttonColor || undefined"
        :flat="flat"
        :round="round"
        :dense="dense"
        :size="size"
        :disable="disable"
        @click.stop
        @mousedown.stop
      >
        <span v-if="iconHtml" class="action-menu__trigger-icon" v-html="iconHtml"></span>
        <component
          v-else-if="triggerLucideName && getLucideIcon(triggerLucideName)"
          :is="getLucideIcon(triggerLucideName)"
          :size="18"
          class="action-menu__trigger-icon lucide-icon-wrapper"
        />
        <KBIcon v-else :icon="triggerKbIcon || icon" :size="18" class="action-menu__trigger-icon" />
        <q-menu
          v-model="menu"
          :anchor="menuAnchor"
          :self="menuSelf"
          :transition-show="transitionShow"
          :transition-hide="transitionHide"
          :content-class="menuContentClass"
          :content-style="menuContentStyle"
        >
          <q-list dense style="min-width: 160px">
            <template v-for="(item, i) in computedItems" :key="i">
              <q-separator v-if="item.separator === true" />
              <q-item v-else clickable :disable="item.disable === true" @click="onItemClick(item)">
                <q-item-section
                  v-if="item.iconHtml || item.lucideName || item.kbIcon || item.icon"
                  avatar
                >
                  <span v-if="item.iconHtml" class="am-icon-html" v-html="item.iconHtml"></span>
                  <component
                    v-else-if="item.lucideName && getLucideIcon(item.lucideName)"
                    :is="getLucideIcon(item.lucideName)"
                    :size="16"
                  />
                  <KBIcon
                    v-else
                    :icon="item.kbIcon || item.icon"
                    :size="16"
                    :class="itemIconClass(item)"
                  />
                </q-item-section>
                <q-item-section :class="itemTextClass(item)">{{ item.label }}</q-item-section>
                <q-item-section v-if="item.checkbox === true" side>
                  <q-checkbox
                    dense
                    :model-value="!!item.checked"
                    @update:model-value="(v) => onCheckboxToggle(item, v)"
                  />
                </q-item-section>
                <q-item-section v-else-if="item.rightIcon || item.selected === true" side>
                  <q-icon :name="item.rightIcon || 'check'" size="xs" />
                </q-item-section>
                <q-tooltip v-if="item.tooltip">{{ item.tooltip }}</q-tooltip>
              </q-item>
            </template>
          </q-list>
        </q-menu>
      </q-btn>
    </slot>
  </div>
</template>

<script setup>
  import { ref, computed, shallowRef, watch } from 'vue'
  import { useUiBackstack } from '@/composables/useUiBackstack'

  const props = defineProps({
    // Custom action items
    items: { type: Array, default: () => [] },
    // Built-in presets
    includeDefaultEdit: { type: Boolean, default: false },
    includeDefaultDelete: { type: Boolean, default: false },
    prependDefaults: { type: Boolean, default: false },

    // Trigger button appearance
    icon: { type: String, default: 'more_vert' },
    size: { type: String, default: 'sm' },
    flat: { type: Boolean, default: true },
    round: { type: Boolean, default: true },
    dense: { type: Boolean, default: true },
    buttonColor: { type: String, default: '' },
    // Optional custom HTML for trigger icon (overrides 'icon' when provided)
    iconHtml: { type: String, default: '' },
    // Optional Lucide icon name for trigger (e.g., 'sword') if using lucide-vue-next
    triggerLucideName: { type: String, default: '' },
    // Optional KB icon string for trigger (e.g., 'lucide:sword', 'material:home', 'fa:dragon')
    triggerKbIcon: { type: String, default: '' },
    disable: { type: Boolean, default: false },
    menuAnchor: { type: String, default: 'bottom right' },
    menuSelf: { type: String, default: 'top right' },
    transitionShow: { type: String, default: 'jump-down' },
    transitionHide: { type: String, default: 'jump-up' },

    // Optional payload echoed back with events
    payload: { type: [Object, Array, String, Number, Boolean, null], default: null },

    // Optional class applied to q-menu content (useful to raise z-index)
    menuContentClass: { type: String, default: '' },
    // Optional inline style applied to q-menu content (to force z-index when needed)
    menuContentStyle: { type: String, default: '' },

    // Backstack integration id (use a stable id per logical menu if needed)
    backstackId: { type: String, default: 'action-menu' }
  })

  const emit = defineEmits(['action', 'edit', 'delete', 'open', 'close'])
  import KBIcon from '@/components/common/KBIcon.vue'

  // Lucide icons support (optional)
  const lucideIcons = shallowRef(null)
  import('lucide-vue-next')
    .then((mod) => {
      lucideIcons.value = mod
    })
    .catch(() => {
      /* noop */
    })

  const getLucideIcon = (name) => {
    if (!lucideIcons.value || !name) return null
    const pascal = String(name)
      .split('-')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join('')
    return lucideIcons.value[pascal] || lucideIcons.value[`${pascal}Icon`] || null
  }

  const menu = ref(false)
  const open = () => (menu.value = true)

  // Emit lifecycle events when menu opens/closes so callers can adjust stacking contexts
  const prevMenu = { val: false }
  watch(menu, (next) => {
    try {
      if (next && !prevMenu.val) emit('open')
      if (!next && prevMenu.val) emit('close')
      prevMenu.val = !!next
    } catch {}
  })

  // Close helper
  const close = () => (menu.value = false)

  // Bind to global UI backstack so Back closes the menu instead of navigating
  const uiBack = useUiBackstack()
  const isOpen = computed(() => !!menu.value)
  const skipNextConsume = ref(false)
  const shouldConsumeOnClose = () => {
    if (skipNextConsume.value) {
      skipNextConsume.value = false
      return false
    }
    return true
  }
  uiBack.bindDialog(isOpen, {
    id: props.backstackId,
    onClose: () => close(),
    consumeOnClose: shouldConsumeOnClose
  })

  const defaultItems = computed(() => {
    const out = []
    if (props.includeDefaultEdit) {
      out.push({ key: 'edit', label: 'Edit', icon: 'edit' })
    }
    if (props.includeDefaultDelete) {
      if (out.length) out.push({ separator: true })
      out.push({ key: 'delete', label: 'Delete', icon: 'delete', danger: true })
    }
    return out
  })

  const computedItems = computed(() => {
    const core = Array.isArray(props.items) ? props.items : []
    return props.prependDefaults
      ? [...defaultItems.value, ...core]
      : [...core, ...defaultItems.value]
  })

  function onItemClick(item) {
    try {
      if (item && item.disable === true) return
      skipNextConsume.value = true
      close()
      const key = item.key || item.event || item.id || item.label
      if (key === 'edit') emit('edit', { key, item, payload: props.payload })
      else if (key === 'delete') emit('delete', { key, item, payload: props.payload })
      else emit('action', { key, item, payload: props.payload })
    } catch {
      /* noop */
    }
  }

  function onCheckboxToggle(item, value) {
    try {
      skipNextConsume.value = true
      close()
      const key = item.key || item.event || item.id || item.label
      emit('action', {
        key,
        item: { ...item, checked: value },
        payload: props.payload,
        checked: value
      })
    } catch {
      /* noop */
    }
  }

  function itemColor(item) {
    if (item.color) return item.color
    if (item.danger) return 'negative'
    return undefined
  }

  function itemTextClass(item) {
    return item.danger ? 'text-negative' : ''
  }

  function itemIconClass(item) {
    if (item.color) return `text-${item.color}`
    if (item.danger) return 'text-negative'
    return ''
  }
</script>

<style scoped></style>
